import { ValidationObj } from "./Grant";
import { DematAccount } from "./VestingTemplate";

export type EmployeesOverviewDto = {
  employees: Employee[];
  departmentEmployeeCountDtos: DepartmentEmployeeCountDto[];
  yearofJoiningEmployeeCountDtos: YearofJoiningEmployeeCountDto[];
};

export interface UploadedData extends EmployeeUploadHeaderResponse {
  newEmployeesToBeAdded: NewEmployeesToBeAdded[];
  duplicateEmployees: DuplicateEmployee[];
}

export interface NewEmployeesToBeAdded {
  "Sl no": number;
  "Employee ID": string;
  "Employee Name": string;
  "Legal Name": string;
  "Status of employment": string;
  "Email address": string;
  "Employment Type": string;
  "Country Residing": string;
  "Registered address": string;
  Manager: string;
  "Employer Name": string;
  Grade: string;
  Designation: string;
  Department: string;
  Contact: string;
  Nationality: string;
  "Date Of Joining": number;
  "Date of Leaving"?: number;
}

export interface DuplicateEmployee {
  id: string;
  createdAt: string;
  updatedAt: string;
  name: string;
  status: string;
  type: string;
  email: string;
  hrId: string;
  isLoginProvided: boolean;
  dateOfJoining: string;
  resignationDate: any;
  lastDay: any;
  companyId: string;
  previousEmployeeId: any;
  isDeactivated: boolean;
  isDeleted: boolean;
}

export type Employee = {
  employeeId?: string;
  id: string;
  employeeName: string;
  employeeIdentificationString: string;
  employeeDesignation: string;
  employmentStatus: string;
  department: string;
  email: string;
  totalOptionsGranted?: number;
  totalOptionsExercised?: number;
  totalOptionsWithdrawn?: number;
  totalPercentageVested: number;
  lastDay?: string;
  dateOfJoin: string;
  isLoginProvided?: boolean;
  lastLogin?: string;
  employmentType: string;
  createdAt?: string;
  updatedAt?: string;
  noOfGrants?: number;
  salary?: number;
  isDeactivated: boolean;
  grade?: string;
  name?: string;
  isResignationSchedule?: boolean;
  pausedGrants?: number;
  allGrants?: { identifier: string; state: string }[];
  tags?: { label: string; value: string }[];
  keyPerson?: boolean;
  companyName?: string;
  resignationDate?: string;
  userEmail?: string;
  personalEmail?: string;
  managers?: string[];
};

export type BulkResignationDto = {
  hrId: string;
  employeeName: string;
  resignationDate?: string;
  lastWorkingDate?: string;
  pauseVesting: string;
  pauseVestingDate?: string;
  lapsedImmediately: string;
  warnings: string;
  errors: string;
  note: string;
};

export type DepartmentEmployeeCountDto = {
  department: string;
  numberOfEmployees: number;
};

export type YearofJoiningEmployeeCountDto = {
  yearofJoining: number;
  numberOfEmployees: number;
};

export interface AddEmployeeReq {
  employeeName: string;
  employeeDesignation: string;
  address: string;
  contactNumber?: string;
  department: string;
  email: string;
  manager: string;
  residentialStatus: string;
  nationality: string;
  legalName: string;
  employeeIdentification: string;
  dateOfJoin: string;
  grade: string;
  employmentStatus: EmployementStatus;
  company: string;
  lastDay?: string;
  notes: string;
  employmentType: EmployementType;
  imageURL?: string;
  pan?: string;
  panCipher?: string;
  personalEmail?: string;
  dematAccounts?: DematAccount[];
  id?: string;
  subsidiaryCompanyId?: string;
  salary?: number;
  resignationDate?: string;
  employeeCount?: number; //Use only for modelling
  tags?: { label: string; value: string }[];
  managerIds?: string[];
  customFieldValues?: Record<string, any>;
  nominees?: EmployeeNominee[];
  boomerangResolution?: BoomerangResolution;
  effectiveDate?: string;
}

export interface ManagerOption {
  userId: string | number;
  fullName: string;
  emailId: string;
}

export type BoomerangResolutionStrategy =
  | "MERGE_EXISTING_EMPLOYEE"
  | "CREATE_SEPARATE_EMPLOYEE"
  | "NOT_BOOMERANG";

export type BoomerangMatchReason =
  | "WORK_EMAIL"
  | "PERSONAL_EMAIL"
  | "EMPLOYEE_ID"
  | "NAME";

type BoomerangResolutionBase = {
  candidateEmployeeId: string;
  expectedCandidateUpdatedAt: string;
  reason?: string;
};

export type BoomerangResolution = BoomerangResolutionBase &
  (
    | { strategy: "NOT_BOOMERANG" }
    | { strategy: "MERGE_EXISTING_EMPLOYEE"; continueVesting: boolean }
    | {
        strategy: "CREATE_SEPARATE_EMPLOYEE";
        moveGrants: boolean;
        continueVesting: boolean;
      }
  );

export interface BoomerangCandidate {
  employeeId: string;
  employeeName: string;
  employeeIdentification: string;
  workEmail: string;
  previousDesignation?: string;
  resignationDate?: string;
  lastWorkingDay?: string;
  updatedAt: string;
  matchReasons: BoomerangMatchReason[];
  alreadyLinked: boolean;
  grantCount: number;
  reversibleLapsedVestingCount: number;
  hasPortalAccess: boolean;
}

export interface EmployeeNominee {
  id?: string;
  name: string;
  relationship: string;
  percentage: number;
  dateOfBirth?: string;
  contact?: string;
  email?: string;
  address?: string;
  guardian?: EmployeeNominee;
}

export type EmployeeProfileImageChange =
  | { operation: "REPLACE"; dataUrl: string }
  | { operation: "REMOVE" };

export type UpdateEmployeeChanges = Partial<
  Omit<
    Pick<
      AddEmployeeReq,
      | "employeeName"
      | "legalName"
      | "email"
      | "personalEmail"
      | "employeeIdentification"
      | "employmentType"
      | "dateOfJoin"
      | "subsidiaryCompanyId"
      | "employeeDesignation"
      | "department"
      | "managerIds"
      | "grade"
      | "salary"
      | "contactNumber"
      | "address"
      | "residentialStatus"
      | "nationality"
      | "notes"
      | "tags"
      | "dematAccounts"
      | "nominees"
    >,
    | "personalEmail"
    | "subsidiaryCompanyId"
    | "employeeDesignation"
    | "grade"
    | "salary"
    | "contactNumber"
    | "address"
    | "notes"
  >
> & {
  personalEmail?: string | null;
  subsidiaryCompanyId?: string | null;
  employeeDesignation?: string | null;
  grade?: string | null;
  salary?: number | null;
  contactNumber?: string | null;
  address?: string | null;
  notes?: string | null;
  customFieldChanges?: Record<string, unknown | null>;
  panCipher?: string;
  removePan?: true;
  profileImage?: EmployeeProfileImageChange;
};

export interface UpdateEmployeeRequest {
  expectedUpdatedAt: string;
  changes: UpdateEmployeeChanges;
  effectiveDate?: string;
}

export type EmployeeHistoryEntity =
  | "Employee"
  | "EmployeeMetaData"
  | "ManagerEmployeeLink"
  | "NomineeDetails";

export interface EmployeeHistoryChange {
  entity: EmployeeHistoryEntity;
  fieldName: string;
  fieldLabel?: string;
  oldValue: unknown;
  newValue: unknown;
}

export interface EmployeeHistoryEvent {
  id: string;
  changeType: "PROFILE_UPDATE" | "BOOMERANG_REJOIN" | "INTERNAL_TRANSFER";
  occurredAt: string;
  effectiveDate: string | null;
  actor: { id: string | null; name: string };
  note: string | null;
  metaData: Record<string, unknown>;
  changes: EmployeeHistoryChange[];
}

export interface RelatedEmploymentSummary {
  relationshipType: "REJOINED_AS";
  employeeId: string;
  employeeName: string;
  occurredAt: string;
}

export interface EmployeeHistoryPage {
  items: EmployeeHistoryEvent[];
  nextCursor: string | null;
  relatedEmployments: RelatedEmploymentSummary[];
}

export type EmployeeHistoryImportRowStatus = "VALID" | "ERROR";

export interface EmployeeHistoryImportRow {
  rowId: string;
  rowNumber: number;
  currentEmployeeId: string;
  employeeName?: string;
  effectiveDate?: string;
  note?: string | null;
  changes: EmployeeHistoryChange[];
  changeType?: "PROFILE_UPDATE" | "INTERNAL_TRANSFER";
  internalTransferTriggerFields?: string[];
  errors: string[];
  status: EmployeeHistoryImportRowStatus;
}

export interface EmployeeHistoryImportReview {
  cacheId: string;
  rows: EmployeeHistoryImportRow[];
  summary: {
    totalRows: number;
    validRows: number;
    errorRows: number;
    internalTransfers: number;
    profileUpdates: number;
  };
}

export interface EmployeeHistoryImportCommitResult {
  importedRows: number;
  rows: Array<{ rowId: string; employeeId: string; employeeName: string }>;
}

export enum EmployementStatus {
  EMPLOYED = "EMPLOYED",
  RESIGNED = "RESIGNED",
  RESIGNATION_SCHEDULED = "RESIGNATION SCHEDULED",
  PENDING_RESIGNATION = "PENDING RESIGNATION",
  RETIRED = "RETIRED",
  TERMINATED = "TERMINATED",
  PENDING = "PENDING",
}

export enum EmployementType {
  EMPLOYEE = "EMPLOYEE",
  CONSULTANT = "CONSULTANT",
}

export interface RevokeAccessRequest {
  empId: string;
  isDeactived: boolean;
}

export interface RevokeAccessResponse {
  reason: string;
}

export interface UploadEmployeeExcel {
  file?: string;
  correctedGrantData?: ValidationObj[][];
  confirm?: boolean;
}

export interface UploadBulkResignationExcel {
  file: string;
}

export interface BulkResignationImportDto {
  file?: string;
  resignationValidatedData?: ValidationObj[][];
  exerciseValidatedData?: ValidationObj[][];
  confirm?: boolean;
}

export interface GrantReversionWarning {
  grantIdentifier: string;
  dateOfGrant: Date;
  optionsToRevert: number;
}

export interface ResignationRowReversionWarning {
  rowIndex: number;
  hrId: string;
  affectedGrants: GrantReversionWarning[];
}

export interface BulkResignationImportResponse {
  hasErrors: boolean;
  status: boolean;
  message: string;
  resignationValidationData: ValidationObj[][];
  exerciseValidationData: ValidationObj[][];
  reversionWarnings?: ResignationRowReversionWarning[];
  summary?: { resignationsInitiated: number; exercisesCreated: number };
}
export interface UploadTaxExcel extends UploadEmployeeExcel {
  validatedTaxDetail?: ValidationObj[][];
}
export interface UploadOfferExcel {
  file: string;
  eventId: string;
}

export interface UploadGrantExcel {
  file: string;
}

export type EmployeeCredentialGeneration = {
  employeeIds: string[];
  regenerateExisting: boolean;
  sendCredentialsGenerationEmail: boolean;
};

export type EmployeeUploadHeaderResponse = {
  headersInFile: string[];
  headersInSystem: string[];
  udfFieldLabels?: Record<string, string>;
  prefilledMapperData?: {
    [k: string]: { type: string; field: string };
  };
  message?: string;
  sampleData?: { [k: string]: any };
  validationErrors?: any;
  cacheId?: string;
  validationData?: ValidationResponse[][];
  validationPending?: boolean;
  savePending?: boolean;
};

export type ValidateAndImportResponse = {
  hasErrors: boolean;
  validationData: ValidationResponse[][];
  validationPending: boolean;
};
export type ValidationResponse = {
  name: string;
  value: string | number;
  error: string | null;
  valid: boolean;
  warning?: string;
};

export type BulkEmployeeImportRowStatus =
  | "VALID"
  | "ERROR"
  | "ACTIVE_DUPLICATE"
  | "BOOMERANG_CANDIDATE"
  | "RESOLVED_BOOMERANG";

export type BulkEmployeeImportRow = {
  rowId: string;
  rowNumber: number;
  employee: Record<string, any>;
  validationData: ValidationResponse[];
  status: BulkEmployeeImportRowStatus;
  candidates: BoomerangCandidate[];
  resolution?: BoomerangResolution;
  selectedCandidateId?: string;
};

export type BulkEmployeeImportValidationResult = {
  rows: BulkEmployeeImportRow[];
  summary: {
    totalRows: number;
    validRows: number;
    errorRows: number;
    boomerangCandidates: number;
    commitEligibleRows: number;
  };
};

export type BulkEmployeeImportCommitResult = {
  summary: {
    importedRows: number;
    newEmployees: number;
    mergedRejoins: number;
    separateRejoins: number;
  };
  rows: Array<{
    rowId: string;
    employeeId: string;
    employeeName: string;
    strategy: BoomerangResolutionStrategy | "NEW_EMPLOYEE";
  }>;
};
export type ImportConfig = {
  id: string;
  name?: string;
  mapperData: {
    [k: string]: { type: string; field: string };
  };
};

export type RevertResignation = {
  employeeIds: string[];
  vestAsPerToday: boolean;
  sendEmailForVestings: boolean;
  resumeFutureVestings: boolean;
  revertAccessBackToPrimaryEmailId: boolean;
  note: string;
  validateResignedState?: boolean;
};

export interface EmployeeMetaDataRow {
  id: string;
  hrId?: { old: string; new: string };
  email?: { old: string; new: string };
  personalEmail?: { old: string; new: string };
  designation?: { old: string; new: string };
  grade?: { old: string; new: string };
  address?: { old: string; new: string };
  type?: { old: string; new: string; values?: string[] };
  dateOfJoining?: { old: Date; new: Date };
  subsidiaryCompany?: { old: string; new: string; values?: string[] };
  manager?: { old: string; new: string };
  contact?: { old: string; new: string };
  pan?: { old: string; new: string };
  department?: { old: string; new: string };
  residentialStatus?: { old: string; new: string };
  nationality?: { old: string; new: string };
  workingCountry?: { old: string; new: string };
  salary?: { old: number; new: number };
  isNewRegime?: { old: boolean; new: boolean };
  taxSlab?: { old: string; new: string };
  surchargePercentage?: { old: number; new: number };
  cessPercentage?: { old: number; new: number };
  dematAccounts?: {
    old: DematAccount[];
    new: DematAccount[];
    replace?: boolean;
  };
  nominees?: {
    old: {
      nominee1?: NomineeDetails;
      nominee2?: NomineeDetails;
      nominee3?: NomineeDetails;
      nominee4?: NomineeDetails;
      nominee5?: NomineeDetails;
      nominee6?: NomineeDetails;
    };
    new: {
      nominee1?: NomineeDetails;
      nominee2?: NomineeDetails;
      nominee3?: NomineeDetails;
      nominee4?: NomineeDetails;
      nominee5?: NomineeDetails;
      nominee6?: NomineeDetails;
    };
  };
  [key: string]: any;
}

export type NomineeGuardian = {
  name?: string;
  relationship?: string;
  contact?: string;
  email?: string;
  dob?: string | null;
  address?: string;
};

export type NomineeDetails = {
  name?: string;
  percentage?: string | number;
  relationship?: string;
  dob?: string | null;
  address?: string;
  contact?: string;
  email?: string;
  guardian?: NomineeGuardian | null;
};
